import { Theme } from '@mui/material';
import LoadingButtonIndicator from '@/components/shared/loading-button-indicator';

/**
 * In MUI 7, @mui/lab's LoadingButton is a deprecation shim that renders the core
 * Button, which only reads `theme.components.MuiButton` — a `MuiLoadingButton`
 * key is silently ignored. The overrides below are keyed under `MuiButton` so
 * loading buttons keep the production look (primary background + custom white
 * indicator). Button only renders `loadingIndicator` when the `loading` prop is
 * set, so the defaultProps entry is inert for regular buttons.
 */
const LoadingButton = (theme: Theme) => {
  return {
    MuiButton: {
      defaultProps: {
        loadingIndicator: <LoadingButtonIndicator />,
      },
      styleOverrides: {
        root: {
          // `.MuiLoadingButton-loading` no longer exists in v7; the core Button
          // applies `.MuiButton-loading` instead (see buttonClasses.js). A loading
          // button is also `.Mui-disabled` (Button sets `disabled || loading`), but
          // this rule still wins over the contained variant's
          // `action.disabledBackground`: theme styleOverrides are serialized after
          // the component's own styles at equal specificity. Kept at (0,2,0) on
          // purpose so per-instance `sx` loading overrides can still take precedence.
          '&.MuiButton-loading': {
            backgroundColor: theme.palette.primary.main,
          },
        },
        contained: {
          // Hide the label under the centered spinner (production behavior).
          // Must live in the `contained` slot: button.ts's contained
          // `&.Mui-disabled { color: white }` is emitted after the core Button's
          // `&.MuiButton-loading { color: transparent }` and would otherwise win
          // the tie, showing white label text under the spinner. This key merges
          // after button.ts's (index.ts merges Button before LoadingButton), so
          // at equal specificity it restores the hidden label while loading;
          // disabled-but-not-loading buttons keep their white label.
          '&.MuiButton-loading': {
            color: 'transparent',
          },
        },
        loadingIndicator: {
          color: theme.palette.common.white,
        },
      },
    },
  };
};
export default LoadingButton;
